import React, { useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import { downloadMyDataExport } from "../../API/profileAPI";

/**
 * Download everything we hold about this account (worklist task 160).
 *
 * The archive is built server-side and can take a moment on a case with many
 * documents, so the button reports that it is working rather than appearing
 * to do nothing.
 */
const DownloadMyData: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await downloadMyDataExport();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `letsgetvisa-export-${new Date()
        .toISOString()
        .slice(0, 10)}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the download a moment to start before the URL is revoked.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      // The endpoint returns a blob, so an error body arrives as one too and
      // has to be read back before it can be shown.
      let message = "Could not prepare your download. Please try again.";
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.error) message = parsed.error;
        } catch {
          /* keep the default */
        }
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 border border-gray-200 rounded-[12px] bg-white mb-5">
      <h5 className="text-xl font-normal mb-2">Your data</h5>
      <p className="text-[14px] text-gray-500 mb-4">
        Download a copy of everything we hold for your account — your details,
        applications, answers, payment references and uploaded documents.
      </p>
      <button
        type="button"
        onClick={handleDownload}
        disabled={loading}
        aria-busy={loading}
        className="w-full border border-[#E5E7EB] rounded-md flex items-center justify-center p-3 text-[16px] bg-white hover:bg-[#F9FAFB] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {/* Fixed-size slot so swapping the icon for the spinner does not
            shift the layout. */}
        <span className="inline-flex items-center justify-center w-[20px] h-[20px] me-3">
          {loading ? (
            <span className="inline-block w-[16px] h-[16px] border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          ) : (
            <FiDownload className="text-[20px]" />
          )}
        </span>
        {loading ? "Preparing your download…" : "Download my data"}
      </button>
    </div>
  );
};

export default DownloadMyData;
